import { useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import * as icons from "react-bootstrap-icons";
import { cancelAccountDeletionAPI } from "../../redux/auth-reducer/auth-service";
import {
  DELETION_CANCELLED_SIGN_IN_FAILED_MESSAGE,
  DELETION_IN_PROGRESS_MESSAGE,
  cancelDeletionFailure,
  deletionNoticeMessage,
} from "../../utils/helper/accountDeletion";

/**
 * #234: what the sign-in page shows instead of the form when the account signing in has a deletion
 * request (Account/Login refused it with ACCOUNT_PENDING_DELETION - only ever after the right
 * password, so nothing is revealed to anyone else).
 *
 * Nobody is signed in here (AC1, AC3): no token is stored, and Back to login, the browser's back
 * button or a reload simply leave it with the request still in place. The password the player
 * typed at sign-in is not kept: Cancel deletion asks for it again (AC4), and it is sent to
 * Account/CancelAccountDeletion with the email they signed in with. On success that answers exactly
 * as Account/Login does, and `onCancelled` signs the player in with it.
 *
 * The dialog's errors are shown inside it, not as toasts: apiClient.js already toasts a request
 * that got no reply, and the player must stay on the dialog to try again (AC17).
 *
 * @param {object} props
 * @param {string} props.email The address the player signed in with.
 * @param {string} props.deletionDueAt `deletionDueAt` of the sign-in refusal (UTC).
 * @param {() => void} props.onBack Back to the sign-in form.
 * @param {(data: object) => Promise<void>} props.onCancelled Signs the player in with the cancel's answer.
 */
export const AccountDeletionNotice = ({ email, deletionDueAt, onBack, onCancelled }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // AC6: the sweep started deleting the account while the notice was open.
  const [inProgress, setInProgress] = useState(false);

  const openDialog = () => {
    setPassword("");
    setError("");
    setVisible(false);
    setShowDialog(true);
  };

  const closeDialog = () => {
    if (busy) return;
    setShowDialog(false);
    setPassword("");
    setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!password || busy) return;

    setBusy(true);
    setError("");
    const res = await cancelAccountDeletionAPI({ email, password });

    if (res?.data?.status === true && res.data.token) {
      try {
        // Stays busy: signing in navigates away from this page.
        await onCancelled(res.data);
      } catch {
        // The request is cancelled; only signing in did not finish. The password stays, so Keep my
        // account tries again: the server signs an account that is already active in, with no
        // second email.
        setBusy(false);
        setError(DELETION_CANCELLED_SIGN_IN_FAILED_MESSAGE);
      }
      return;
    }

    const failure = cancelDeletionFailure(res);
    setBusy(false);
    if (failure.clearPassword) setPassword("");
    if (failure.inProgress) {
      setInProgress(true);
      setShowDialog(false);
      return;
    }
    setError(failure.message);
  };

  return (
    <section aria-labelledby="deletion-notice-title">
      <h1 className="heading" id="deletion-notice-title">
        Account scheduled for deletion
      </h1>
      {inProgress ? (
        <Alert variant="danger">{DELETION_IN_PROGRESS_MESSAGE}</Alert>
      ) : (
        <p>{deletionNoticeMessage(deletionDueAt)}</p>
      )}
      <div className="d-flex flex-wrap gap-2 mt-4">
        {!inProgress && (
          <Button className="submit-btn" onClick={openDialog}>
            Cancel deletion
          </Button>
        )}
        <Button variant="outline-secondary" onClick={onBack}>
          Back to login
        </Button>
      </div>

      <Modal
        show={showDialog}
        centered
        onHide={closeDialog}
        backdrop={busy ? "static" : true}
        keyboard={!busy}
      >
        <Form onSubmit={submit} noValidate>
          <Modal.Header className="promo-code-header promo-close" closeButton={!busy}>
            <Modal.Title>
              <b>Keep your account?</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Enter your password to cancel the deletion request. Your account will stay active.</p>
            <Form.Group controlId="cancelDeletionPassword">
              <Form.Label>Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={visible ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your Password"
                  disabled={busy}
                  autoFocus
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setVisible(!visible)}
                  aria-label={visible ? "Hide password" : "Show password"}
                  disabled={busy}
                >
                  {visible ? <icons.Eye /> : <icons.EyeSlash />}
                </Button>
              </InputGroup>
            </Form.Group>
            {error && (
              <Alert variant="danger" className="mt-3 mb-0" role="alert">
                {error}
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDialog} disabled={busy}>
              Go back
            </Button>
            <Button type="submit" variant="primary" disabled={!password || busy}>
              {busy ? "Keeping your account..." : "Keep my account"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
};
